import { Badge, Box, Tab, Tabs } from "@mui/material";
import React, { useMemo } from "react";
import { a11yProps, TabPanel } from "../App/Components/TabGroup/TabGroup";
import { useImportTabStore } from "../App/State/Stores/ImportTabStore";
import { IImportStatus } from "../App/Typings/Imports";
import { ITabPanel } from "../App/Typings/TabPanel";
import createSlug from "../App/Utilities/createSlug";
import { ConflictsTab } from "./ConflictsTab";
import { ErrorsTab } from "./ErrorsTab";
import { ImportTable } from "./ImportsTab";

export default function ImportLogTabs({ importStatus }: { importStatus: IImportStatus }) {
  const pendingCount = importStatus?.pendingConflicts ?? 0;
  const showConflictBadge = pendingCount > 0;
  const labels = useMemo(() => getTabLabels(importStatus), [importStatus]);
  const tabValue = useImportTabStore((state) => state.tabValue);
  const setTabValue = useImportTabStore((state) => state.setTabValue);
  const resetPagination = useImportTabStore((state) => state.resetPagination);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    resetPagination();
  };

  const tabs = [
    {
      slug: "imports",
      label: labels[0],
      TabComponent: <ImportTable statusId={importStatus.id} />,
    },
    {
      slug: "conflicts",
      label: showConflictBadge ? (
        <Badge
          variant="dot"
          color="warning"
          title={`You have ${pendingCount} conflict${pendingCount > 1 ? "s" : ""} to resolve`}
        >
          {labels[1]}
        </Badge>
      ) : (
        labels[1]
      ),
      TabComponent: <ConflictsTab statusId={importStatus.id} />,
    },
    {
      slug: "errors",
      label: labels[2],
      TabComponent: <ErrorsTab statusId={importStatus.id} />,
    },
  ] as (ITabPanel & { slug: string })[];

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginBottom: "1rem",
        }}
      >
        <Tabs value={tabValue} onChange={handleChange} aria-label={"Import Status"}>
          {tabs.map((tab, idx) => (
            <Tab
              key={idx}
              label={tab.label}
              data-testid={createSlug(tab.slug)}
              {...a11yProps(createSlug("Import Status"), idx)}
            />
          ))}
        </Tabs>
      </Box>
      {tabs.map((tab, idx) => (
        <TabPanel value={tabValue} index={idx} key={idx}>
          {typeof tab.TabComponent === "object" ? tab.TabComponent : <tab.TabComponent />}
        </TabPanel>
      ))}
    </Box>
  );
}

function getTabLabels(importStatus: IImportStatus) {
  return [
    getCountLabel("Imports", importStatus.totalSuccesses),
    getCountLabel("Conflicts", importStatus.pendingConflicts),
    getCountLabel("Errors", importStatus.errorCount),
  ];
}

function getCountLabel(base: string, count: number) {
  if (count <= 0) {
    return base;
  }
  return `${base} (${count})`;
}
